export default {
  index: {
    title: 'Guide alle maree e alle correnti',
    description:
      'Scopri come funzionano le maree e le correnti di marea: coefficienti di marea, correnti di flusso e riflusso, piani di riferimento delle maree (datum), e come si calcolano le previsioni delle maree.',
    subtitle:
      'Brevi spiegazioni in linguaggio semplice sulla scienza che sta dietro alle previsioni di maree e correnti.',
  },
  back: 'Torna a tutte le guide',
  guides: {
    'tidal-coefficients': {
      title: 'I coefficienti di marea spiegati',
      description:
        "Cosa significano i coefficienti di marea: la scala da 20 a 120, come le maree sizigiali e di quadratura sono legate alla Luna, e perché un coefficiente più alto produce un'escursione di marea maggiore.",
      blurb:
        'La scala da 20 a 120 che riassume quanto sarà grande una marea e come la Luna la governa.',
      body: [
        {
          p: "Un coefficiente di marea è un singolo numero che riassume quanto sarà grande una marea in un dato giorno. È ampiamente utilizzato in Francia e in altri paesi che seguono la tradizione idrografica francese. Il coefficiente è adimensionale e va normalmente da circa 20 a 120: più alto è il numero, maggiore è l'escursione tra l'alta e la bassa marea, e più forti sono le correnti di marea associate.",
        },
        { h2: 'La scala da 20 a 120' },
        {
          p: "Il coefficiente confronta l'escursione di marea del giorno con un riferimento a lungo termine. Diversi punti della scala hanno significati convenzionali:",
        },
        {
          list: [
            {
              term: '20',
              text: "le maree più piccole, prossime all'escursione astronomica minima.",
            },
            { term: '45', text: "una marea di quadratura media, quando l'escursione è piccola." },
            {
              term: '70',
              text: "una marea media, all'incirca a metà strada tra le quadrature e le sizigie.",
            },
            { term: '95', text: "una marea sizigiale media, quando l'escursione è grande." },
            {
              term: '120',
              text: "le maree più grandi, prossime all'escursione astronomica massima.",
            },
          ],
        },
        { h2: 'Perché cambia: la Luna' },
        {
          p: "I coefficienti salgono e scendono secondo il ciclo sizigie-quadrature di circa due settimane. Le maree sizigiali, con coefficienti elevati, si verificano un giorno o due dopo ogni Luna nuova e piena, quando il Sole e la Luna si allineano e le loro forze di marea si sommano. Le maree di quadratura, con coefficienti bassi, si verificano in prossimità del primo e dell'ultimo quarto, quando il Sole e la Luna formano un angolo retto e si annullano in parte.",
        },
        {
          p: "Conta anche la distanza della Luna. Quando una marea sizigiale coincide con il perigeo (il punto di massima vicinanza della Luna alla Terra), il coefficiente raggiunge i valori più alti. Le maree in assoluto più grandi tendono a concentrarsi in prossimità degli equinozi di primavera e d'autunno.",
        },
        { h2: 'Cosa un coefficiente non ti dice' },
        {
          p: "Il coefficiente descrive soltanto l'escursione astronomica. Non include l'effetto del tempo meteorologico: il vento e la pressione atmosferica possono innalzare o abbassare il livello reale dell'acqua indipendentemente dal coefficiente. È inoltre una proprietà della marea nel suo complesso per quel giorno, non un'altezza in metri o piedi in un singolo luogo.",
        },
      ],
    },
    'tidal-currents': {
      title: 'Flusso, riflusso e stanca: come funzionano le correnti di marea',
      description:
        "Come funzionano le correnti di marea: la direzione del flusso e del riflusso, la stanca al momento dell'inversione, la velocità della corrente in nodi e la sua direzione (il set), e perché la stanca non sempre coincide con l'alta e la bassa marea.",
      blurb:
        "Il flusso, il riflusso e la stanca, e perché la corrente più forte non è sempre all'alta marea.",
      body: [
        {
          p: "Le correnti di marea sono il movimento orizzontale dell'acqua che accompagna l'innalzamento e l'abbassamento verticale della marea. Mentre l'altezza della marea indica quanto è profonda l'acqua, la corrente indica in che direzione scorre e con quale velocità. Le correnti contano soprattutto nei canali, nelle imboccature e negli estuari, dove possono raggiungere diversi nodi.",
        },
        { h2: 'Flusso, riflusso e stanca' },
        {
          list: [
            {
              term: 'Corrente di flusso',
              text: 'acqua che scorre verso la costa o risale un estuario, generalmente mentre la marea sale.',
            },
            {
              term: 'Corrente di riflusso',
              text: 'acqua che scorre verso il mare aperto o discende un estuario, generalmente mentre la marea scende.',
            },
            {
              term: 'Stanca',
              text: 'la breve pausa in cui la corrente è prossima allo zero mentre si inverte tra flusso e riflusso.',
            },
          ],
        },
        {
          p: "La velocità della corrente si misura in nodi (miglia nautiche all'ora). La sua direzione, chiamata set, è indicata come un rilevamento alla bussola: la direzione verso cui scorre l'acqua.",
        },
        { h2: "La stanca non è sempre all'alta marea" },
        {
          p: "Si suppone spesso che la corrente si fermi all'alta e alla bassa marea. Questo è vero solo in alcuni luoghi. Il momento dipende da come si comporta localmente l'onda di marea. Dove la marea si comporta come un'onda progressiva, come in un lungo canale aperto, il flusso e il riflusso più forti si verificano in prossimità dell'alta e della bassa marea, e la stanca cade vicino al punto intermedio. Dove si comporta come un'onda stazionaria, come in fondo a una baia chiusa, avviene il contrario: la corrente più veloce si ha a metà marea e la stanca si verifica in prossimità dell'alta e della bassa marea. Le tavole delle correnti locali sono la guida affidabile.",
        },
        { h2: 'Correnti alternate e rotatorie' },
        {
          p: "Nei canali stretti la corrente è di solito alternata: scorre in un senso durante il flusso, poi si inverte e torna indietro durante il riflusso. In acque costiere aperte la corrente è spesso rotatoria: cambia direzione con continuità lungo tutta la bussola nel corso di un ciclo di marea, anziché invertirsi lungo un'unica linea.",
        },
      ],
    },
    'tidal-datums': {
      title: 'Piani di riferimento delle maree e altezze di marea',
      description:
        "Da cosa si misura un'altezza di marea: lo zero idrografico, la più bassa marea astronomica (LAT), il livello medio delle basse maree inferiori (MLLW), e perché i piani di riferimento differiscono da paese a paese.",
      blurb:
        "Lo zero idrografico, LAT, MLLW, e cosa significa davvero il numero di un'altezza di marea.",
      body: [
        {
          p: "Ogni altezza di marea è una misura al di sopra di un livello di riferimento chiamato piano di riferimento (datum). Quando una tavola di marea indica che l'acqua raggiungerà 1.8 metri, significa 1.8 metri al di sopra del piano di riferimento di marea locale, non al di sopra del fondale né al di sopra del livello del mare nel senso comune. Conoscere il piano di riferimento è ciò che rende significativa un'altezza.",
        },
        { h2: "Cos'è un piano di riferimento di marea" },
        {
          p: "Un piano di riferimento di marea è una quota di riferimento definita da una particolare fase della marea, mediata su un lungo periodo: convenzionalmente circa 19 anni, il che leviga il ciclo di 18.6 anni dell'orbita della Luna. Poiché è una media di osservazioni reali, un piano di riferimento è specifico di un luogo e dell'autorità che lo ha calcolato.",
        },
        { h2: 'Lo zero idrografico e i riferimenti comuni' },
        {
          p: 'Le carte nautiche usano un piano di riferimento di bassa marea affinché la profondità reale sia quasi sempre almeno pari alla profondità riportata sulla carta. Questo zero idrografico è lo zero da cui si contano sia le profondità cartografate sia le altezze di marea. Paesi diversi adottano piani di riferimento diversi:',
        },
        {
          list: [
            {
              term: 'LAT (più bassa marea astronomica)',
              text: 'il livello più basso prevedibile con tempo meteorologico medio; usato dal Regno Unito e da molte altre nazioni.',
            },
            {
              term: 'MLLW (livello medio delle basse maree inferiori)',
              text: 'la media della più bassa delle due basse maree di ogni giorno; lo zero idrografico standard negli Stati Uniti.',
            },
            {
              term: 'MSL (livello medio del mare)',
              text: "il livello medio dell'acqua a lungo termine, usato principalmente come riferimento per le quote terrestri anziché per le carte.",
            },
          ],
        },
        {
          p: "I piani di riferimento di alta marea, come il livello medio delle alte maree (MHW) e il livello medio delle alte maree superiori (MHHW), servono allo scopo opposto, ad esempio per definire una linea di costa o il tirante d'aria di un ponte.",
        },
        { h2: 'Perché le altezze non sono direttamente comparabili' },
        {
          p: "Poiché i piani di riferimento differiscono, un'altezza a una stazione non può essere confrontata direttamente con un'altezza a un'altra a meno che entrambe non usino lo stesso riferimento. Una bassa marea di 0.5 metri misurata da LAT non è lo stesso livello dell'acqua di una bassa marea di 0.5 metri misurata da MLLW. È anche per questo che la stessa marea fisica può essere descritta con numeri diversi nelle tavole di paesi diversi.",
        },
      ],
    },
    'how-tide-predictions-work': {
      title: 'Come si calcolano le previsioni delle maree',
      description:
        "Come vengono previste le maree: il forzante gravitazionale della Luna e del Sole, le componenti armoniche come M2 e S2, e perché i livelli dell'acqua osservati differiscono dalla previsione astronomica.",
      blurb:
        "L'analisi armonica, le componenti che stanno dietro la marea, e perché il tempo meteorologico fa divergere la realtà.",
      body: [
        {
          p: "Le maree possono essere previste con anni di anticipo perché sono governate dai moti della Luna e del Sole, noti con grande precisione. La marea in un luogo è la risposta dell'oceano a quel forzante gravitazionale, plasmata dalla costa e dalla profondità locali. Il metodo che trasforma l'astronomia in una tavola di marea si chiama analisi armonica.",
        },
        { h2: 'La marea come somma di componenti' },
        {
          p: "L'analisi armonica tratta la marea come la somma di molte onde semplici, ciascuna chiamata componente. Ogni componente ha una frequenza fissata dall'astronomia (il modo in cui la Terra, la Luna e il Sole si muovono l'uno rispetto all'altro) mentre la sua ampiezza e il suo sfasamento si ricavano adattando le componenti a un periodo di osservazioni reali del livello dell'acqua in quel luogo. Le principali componenti includono:",
        },
        {
          list: [
            {
              term: 'M2',
              text: 'la componente semidiurna lunare principale, di periodo 12.42 ore; di solito la maggiore.',
            },
            {
              term: 'S2',
              text: 'la componente semidiurna solare principale, di periodo 12.00 ore.',
            },
            { term: 'N2', text: "una componente lunare dovuta all'orbita ellittica della Luna." },
            {
              term: 'K1 e O1',
              text: "le principali componenti diurne, che rendono un'alta marea del giorno maggiore dell'altra in molti luoghi.",
            },
          ],
        },
        {
          p: 'Il familiare ciclo sizigie-quadrature emerge naturalmente da questo: M2 e S2 hanno periodi leggermente diversi, perciò entrano ed escono di fase, rinforzandosi ogni 14.8 giorni per produrre le sizigie e annullandosi in parte per produrre le quadrature.',
        },
        { h2: 'Prevedere in avanti' },
        {
          p: "Una volta note l'ampiezza e la fase di ciascuna componente per un luogo, prevedere la marea è semplicemente questione di valutare la somma in un qualsiasi istante futuro. Le frequenze astronomiche non cambiano mai, quindi il calcolo è deterministico e può essere eseguito con decenni di anticipo.",
        },
        { h2: 'Perché la marea reale differisce' },
        {
          p: "Una previsione è soltanto la marea astronomica. Non include il tempo meteorologico. Venti forti, pressione atmosferica bassa o alta, forti piogge e la portata dei fiumi possono innalzare o abbassare il livello reale dell'acqua: un sovralzo da tempesta può aggiungere un metro o più sopra la previsione, mentre l'alta pressione e i venti da terra possono mantenere l'acqua ben al di sotto. Le previsioni sono quindi un'ottima guida per il momento e l'ampiezza della marea, ma il livello osservato quel giorno può differire.",
        },
      ],
    },
  },
}
